import type { Model } from '@ricoh/ria.client.core/model';
import type { Fetcher } from '@ricoh/ria.client.core/plugins/swr/fetcher';
import type { Except, ValueOf } from 'type-fest';

import { createLogger } from '@ricoh/ria.client.core/plugins/app/logger';
import { isNotNull, isNull } from '@ricoh/ria.client.core/utils/data/object';

import type {
  Batch,
  BatchFieldWithValue,
  Document,
  DocumentFieldWithValue,
  FieldMetadataOCRFieldPositions,
  FieldMetadataOCRFieldPositionsTable,
  FieldMetadataOCRPosition,
  FieldValue,
  OCRField,
  OCRFieldPositionsGeneral,
  OCRFieldPositionsTable,
  OCRPosition,
} from '~/common/model/api/batch';
import type { DataType, DataTypeTable } from '~/common/model/api/common';

import processLogic from '~/common/logic/feature/process';
import fieldLogic from '~/common/logic/feature/settings/batch-type-library/field';
import { prepareConditionOperator, prepareDataType } from '~/services/server/api/common/mapper';

import type { GetBatchFieldsResponse, GetDocumentFieldsResponse } from '../_fetcher.type';
import type { TransformFieldOCRs, TransformFieldValues, TransformOCRField } from './_field.type';

const logger = createLogger('services/server/api/batch');

/**
 * Prepare BatchFields received from service.
 *
 * @param {Batch} batch Batch.
 * @param {Fetcher<string, Model<ValueOf<GetBatchFieldsResponse>>[]> | undefined} getBatchFieldsFetcher GetBatchFields fetcher.
 * @param {TransformFieldValues} fieldValues BatchField Values.
 *
 * @returns {BatchFieldWithValue[]} BatchFields.
 */
export async function prepareBatchFields(
  batch: Batch,
  getBatchFieldsFetcher: Fetcher<string, Model<ValueOf<GetBatchFieldsResponse>>[]>,
  fieldValues: TransformFieldValues,
): Promise<BatchFieldWithValue[]> {
  // * Get BatchFields
  const batchFields = await getBatchFieldsFetcher.handler(batch.id);

  // * Mapping `batchFields` and `fieldValues`
  return batchFields.map((field) => {
    const batchField: BatchFieldWithValue = {
      ...field,
      value: fieldValues ? fieldValues[field.id] : undefined,
      dependent: {
        fields: field.dependent?.fields ?? [],
        documents: Object.entries(field.dependent?.documents ?? {}).map(([dependentDocumentTypeId, dependentDocumentType]) => ({
          id: dependentDocumentTypeId,
          fields: dependentDocumentType.fields ?? [],
        })),
      },
    };

    // * Field type not match current value type
    if (batchField.value?.type && field.type.name !== batchField.value.type) {
      batchField.value.value = undefined;
    }

    // * Prepare metadata
    if (batchField.value?.metadata) {
      batchField.value.metadata.condition = batchField.value.metadata.condition?.map((descriptor) => {
        const operator = prepareConditionOperator(descriptor.operator);
        if (!operator) {
          logger.writeWithData('warn', 'Not found ConditionOperator', { descriptor });
          return descriptor;
        }

        return {
          ...descriptor,
          operator,
        };
      });
    }

    // * Prepare type
    batchField.type = prepareDataType(batchField.type);

    return batchField;
  });
}

/**
 * Prepare DocumentField Confidence.
 *
 * @param {TransformFieldOCRs} fieldOCRs Field OCRs.
 * @param {Except<DocumentFieldWithValue, 'ocr'>} documentField DocumentField with Value.
 *
 * @returns {Pick<OCRField, 'isValidConfidence' | 'confidence'>} OCR Field Confidence.
 */
function prepareOCRFieldConfidence(
  fieldOCRs: TransformFieldOCRs,
  documentField: Except<DocumentFieldWithValue, 'ocr'>,
): Pick<OCRField, 'isValidConfidence' | 'confidence'> {
  const { confidences } = documentField.value?.metadata ?? {};
  if (!confidences?.length) {
    return {
      isValidConfidence: true,
      confidence: undefined,
    };
  }

  // * Average confidence
  const confidence = confidences.reduce((prev, item) => prev + (isNull(item.confidence) ? 0 : item.confidence), 0) / confidences.length;
  const isValidConfidence = fieldOCRs[documentField.id]?.isValidConfidence;
  if (isNotNull(isValidConfidence)) {
    return {
      isValidConfidence,
      confidence,
    };
  }

  // * Default confidence threshold = 100 -> is validC when `confidence` >= `confidenceThreshold`
  const confidenceThreshold = isNull(documentField.confidenceThreshold) ? 100 : documentField.confidenceThreshold;
  return {
    isValidConfidence: confidence >= confidenceThreshold,
    confidence,
  };
}

/**
 * Prepare OCRPosition.
 *
 * @param {Document} document Document.
 * @param {FieldMetadataOCRPosition} position Field Metadata OCRPosition.
 *
 * @returns {OCRPosition} OCRPosition.
 */
function preparePosition(document: Document, position: FieldMetadataOCRPosition): OCRPosition {
  // * Migrate `pageIndex` to `pageId`
  if (!position.pageId) {
    const page = document.pages.at(position.pageIndex ?? 0);
    if (page) {
      position.pageId = page.id;
    }
  }

  if (processLogic.ocr.isPosition(position, 'natifAI')) {
    return {
      ...position,
      boxes: position.boxes?.map(processLogic.ocr.normalizeBox) ?? [],
    };
  }

  if (processLogic.ocr.isPosition(position, 'azureAI')) {
    return {
      ...position,
      regions: position.regions ?? [],
    };
  }

  return position as OCRPosition;
}

/**
 * Prepare OCRField Positions General.
 *
 * @param {Document} document Document.
 * @param {DataTypeTable} type Field DataType.
 * @param {FieldMetadataOCRFieldPositions} positions Field Metadata OCRField Positions.
 *
 * @returns {OCRFieldPositionsGeneral} OCRField Positions General.
 */
function prepareOCRFieldPositionsGeneral(document: Document, type: DataType, positions: FieldMetadataOCRFieldPositions): OCRFieldPositionsGeneral {
  if (processLogic.ocr.isPositionsGeneral(type, positions)) {
    return positions.map((position) => preparePosition(document, position));
  }

  return [];
}

/**
 * Prepare OCRField Positions Table.
 *
 * @param {Document} document Document.
 * @param {DataTypeTable} type Field DataType.
 * @param {FieldValue | undefined} value Field value.
 * @param {FieldMetadataOCRFieldPositions} positions Field Metadata OCRField Positions.
 *
 * @returns {OCRFieldPositionsTable} OCRField Positions Table.
 */
function prepareOCRFieldPositionsTable(
  document: Document,
  type: DataTypeTable,
  value: FieldValue | undefined,
  positions: FieldMetadataOCRFieldPositions,
): OCRFieldPositionsTable {
  const [firstPosition] = positions;
  if (!firstPosition) {
    if (!value || value.type !== 'table') {
      return [];
    }

    return (
      value.value?.map(
        () =>
          type.columns?.reduce(
            (prev, _column, index) => ({
              ...prev,
              [index]: [],
            }),
            {},
          ) ?? {},
      ) ?? []
    );
  }

  const positionsTable = positions as FieldMetadataOCRFieldPositionsTable;
  return positionsTable.map((positionRow) =>
    Object.entries(positionRow).reduce(
      (prev, [key, cellPositions]) => ({
        ...prev,
        [Number(key)]: cellPositions.map((position) => preparePosition(document, position)),
      }),
      {},
    ),
  );
}

/**
 * Prepare OCRField positions.
 *
 * @param {Document} document Document.
 * @param {Except<DocumentFieldWithValue, 'ocr'>} documentField DocumentField with Value.
 *
 * @returns {Pick<OCRField, 'positions'>} OCRField positions.
 */
function prepareOCRFieldPositions(document: Document, { type, value }: Except<DocumentFieldWithValue, 'ocr'>): Pick<OCRField, 'positions'> {
  const { positions = [] } = value?.metadata ?? {};

  if (fieldLogic.isDataType.table(type)) {
    return {
      positions: prepareOCRFieldPositionsTable(document, type, value, positions),
    };
  }

  return {
    positions: prepareOCRFieldPositionsGeneral(document, type, positions),
  };
}

/**
 * Prepare DocumentFields received from service.
 *
 * @param {Document} document Document.
 * @param {Model<ValueOf<GetDocumentFieldsResponse>>[]} documentFields DocumentFields.
 * @param {TransformFieldValues} fieldValues DocumentField Values.
 * @param {TransformFieldOCRs} fieldOCRs Field OCRs.
 *
 * @returns {DocumentFieldWithValue[]} DocumentFields.
 */
export function prepareDocumentFields(
  document: Document,
  documentFields: Model<ValueOf<GetDocumentFieldsResponse>>[],
  fieldValues: TransformFieldValues,
  fieldOCRs: TransformFieldOCRs,
): DocumentFieldWithValue[] {
  // * Mapping `documentFields` and `fieldValues`
  return documentFields.map<DocumentFieldWithValue>((field) => {
    const documentField: Except<DocumentFieldWithValue, 'ocr'> = {
      ...field,
      value: fieldValues ? fieldValues[field.id] : undefined,
      dependent: {
        fields: field.dependent?.fields ?? [],
        documents: Object.entries(field.dependent?.documents ?? {}).map(([dependentDocumentTypeId, dependentDocumentType]) => ({
          id: dependentDocumentTypeId,
          fields: dependentDocumentType.fields ?? [],
        })),
      },
    };

    // * Field type not match current value type
    if (documentField.value?.type && field.type.name !== documentField.value.type) {
      documentField.value.value = undefined;
    }

    // * Prepare metadata
    if (documentField.value?.metadata) {
      documentField.value.metadata.condition = documentField.value.metadata.condition?.map((descriptor) => {
        const operator = prepareConditionOperator(descriptor.operator);
        if (!operator) {
          logger.writeWithData('warn', 'Not found ConditionOperator', { descriptor });
          return descriptor;
        }

        return {
          ...descriptor,
          operator,
        };
      });
    }

    // * Prepare type
    documentField.type = prepareDataType(documentField.type);

    return {
      ...documentField,

      // * Prepare OCR
      ocr: {
        ...prepareOCRFieldPositions(document, documentField),
        ...prepareOCRFieldConfidence(fieldOCRs, documentField),
      },
    };
  });
}

/**
 * Transform OCR Field.
 *
 * @param {Record<string, string>} pageIdMapping Page NewId mapping.
 * @param {OCRField} ocr OCR Field.
 *
 * @returns {TransformOCRField} Transformed OCR Field.
 */
export function transformOCRField(pageIdMapping: Record<string, string>, { type, ocr }: DocumentFieldWithValue): TransformOCRField {
  if (!ocr) {
    return {};
  }

  const mapPageId = ({ pageId, ...position }: OCRPosition) => ({
    pageId: pageIdMapping[pageId] ?? pageId,
    ...position,
  });

  if (processLogic.ocr.isPositionsGeneral(type, ocr.positions)) {
    return {
      ...ocr,
      positions: ocr.positions.map(mapPageId),
    };
  }

  return {
    ...ocr,
    positions: ocr.positions.map((positionRow) =>
      Object.entries(positionRow).reduce(
        (prev, [key, cellPositions]) => ({
          ...prev,
          [Number(key)]: cellPositions.map(mapPageId),
        }),
        {},
      ),
    ),
  };
}
